import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Grid, TextField, Button, Snackbar, Alert } from "@mui/material";
import Menu from "./Menu";
import { ref, push } from 'firebase/database';
import { database } from '../firebase';

interface FormData {
  name: string;
  email: string;
  phone: string;
  city: string;
  message: string;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  city?: string;
  message?: string;
}

const Associate: React.FC = () => {
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (contentContainerRef.current) {
        const contentRect = contentContainerRef.current.getBoundingClientRect();
        setIsSticky(contentRect.top <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: ''
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const sanitizeInput = (input: string): string => {
    return input.replace(/[<>]/g, '').trim();
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters long';
      isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit Indian phone number';
      isValid = false;
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = 'City is required';
      isValid = false;
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
      isValid = false;
    } else if (formData.message.length < 10) {
      errors.message = 'Message must be at least 10 characters long';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors before submitting.',
        severity: 'error'
      });
      return;
    }

    try {
      const associateRequestsRef = ref(database, 'associateRequests');
      const newAssociateRequest = {
        ...formData,
        createdOn: new Date().toISOString()
      };
      await push(associateRequestsRef, newAssociateRequest);

      setSnackbar({
        open: true,
        message: 'Your request has been submitted successfully!',
        severity: 'success'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        message: ''
      });
      setValidationErrors({});
    } catch (error) {
      console.error('Error submitting associate request:', error);
      setSnackbar({
        open: true,
        message: 'Error submitting request. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div ref={contentContainerRef}>
      {/* Sticky Menu */}
      <Box
        sx={{
          width: "100%",
          height: "50vh",
          position: "relative",
          overflow: "hidden",
          background: `
            linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)),
            url(${"/assets/service_01.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Menu isSticky={isSticky} />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <Typography variant="h3" fontWeight="bold" letterSpacing={1}>
            Become an Associate
          </Typography>
        </Box>
      </Box>

      {/* New Content Below */}
      <Box sx={{ padding: "40px 20px", backgroundColor: "#f9f9f9", color: "#000" }}>
        {/* Why Become an Associate */}
        <Box sx={{ maxWidth: "900px", margin: "0 auto", mb: 8 }}>
          <Typography
            variant="h4"
            align="center"
            fontWeight="bold"
            gutterBottom
            sx={{ fontSize: { xs: "28px", md: "36px" }, mb: 4 }}
          >
            Why Become an Associate?
          </Typography>
          <Typography variant="body1" paragraph align="center">
            Partner with <strong>GetMyShares</strong> and join a growing network of associates helping clients reclaim lost shares and unclaimed investments. 
            As our associate, you can build a rewarding business while making a real difference.
          </Typography>
          <Typography variant="body1" paragraph align="center">
            We offer comprehensive backend support, training, marketing tools, and a trusted platform. With us, you focus on growing your network, and we take care of the execution!
          </Typography>
        </Box>

        {/* Associate Form */}
        <Grid container spacing={4} sx={{ maxWidth: "900px", margin: "0 auto" }}>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ mb: 3, textAlign: "center", fontSize: { xs: "24px", md: "28px" }, color: "#2C387E" }}
            >
              Apply to Become an Associate
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              error={!!validationErrors.phone}
              helperText={validationErrors.phone}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ marginRight: "8px", color: "#000000" }}>+91</Typography>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              error={!!validationErrors.city}
              helperText={validationErrors.city}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Message / Background (Optional)"
              name="message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange}
              error={!!validationErrors.message}
              helperText={validationErrors.message}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} textAlign="center">
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#2C387E",
                color: "#fff",
                padding: "10px 30px",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#1E2B6B" },
              }}
            >
              Submit Application
            </Button>
          </Grid>
        </Grid>

        {/* Snackbar Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default Associate;
